import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { SetCurrentAccountAction } from '@app/app-state/actions/current-account.action';
import { AppState } from '@app/app-state/models/app-state';
import { AccountSummary } from '@models/account-summary.model';
import { Account } from '@models/account.model';
import { FileSystemItem } from '@models/file-system-item.model';
import { Store } from '@ngrx/store';
import { LegalEntityDataService } from '@services/legal-entity-data.service';
import { forkJoin, Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { Contact } from '../models/contact.model';
import { DataService } from './data.service';

@Injectable()
export class AccountDataService extends DataService {
  apiPath = 'api/accounts/';

  constructor(
    private http: HttpClient,
    private legalEntityDataService: LegalEntityDataService,
    private store: Store<AppState>
  ) {
    super();
  }

  getAccount(accountId: string): Observable<Account> {
    return this.http
      .get<Account>(this.apiPath + accountId, { headers: this.headers })
      .pipe(catchError(this.handleError));
  }

  getCurrentAccountContacts(): Observable<Contact[]> {
    return this.http
      .get<Contact[]>(this.apiPath + 'current/contacts', { headers: this.headers })
      .pipe(catchError(this.handleError));
  }

  getAutocomplete(search: string): Observable<any[]> {
    return this.http
      .get<any[]>(this.apiPath + `autocomplete?name=${search}`, { headers: this.headers })
      .pipe(catchError(this.handleError));
  }

  getCurrentAccount() {
    return this.http
      .get<Account>(this.apiPath + 'current', { headers: this.headers })
      .pipe(catchError(this.handleError));
  }

  loadCurrentAccountToStore(id: string) {
    return forkJoin(this.getAccount(id), this.legalEntityDataService.getBusinessProfileSummary()).pipe(
      map((data) => {
        const account: Account = data[0];
        account.legalEntity = data[1].length ? data[1][0] : null;
        this.store.dispatch(new SetCurrentAccountAction({ ...account } as Account));
        return account;
      })
    );
  }

  getBCeID() {
    return this.http.get(this.apiPath + 'bceid', { headers: this.headers }).pipe(catchError(this.handleError));
  }

  updateAccount(accountModel: Account) {
    return this.http
      .put(this.apiPath + accountModel.id, accountModel, { headers: this.headers })
      .pipe(catchError(this.handleError));
  }

  deleteAccount(accountModel: Account) {
    return this.http
      .post(this.apiPath + accountModel.id + '/delete', accountModel, { headers: this.headers })
      .pipe(catchError(this.handleError));
  }

  deleteCurrentAccount() {
    return this.http
      .post(this.apiPath + 'delete/current', {}, { headers: this.headers })
      .pipe(catchError(this.handleError));
  }

  /**
   * Get a file list of documents attached to the account by ID and document type
   * @param accountId The account ID to query for documents
   * @param documentType The document type (e.g. "Notice" for inspection notices under the account)
   */
  getFilesAttachedToAccount(accountId: string, documentType: string): Observable<FileSystemItem[]> {
    const headers = new HttpHeaders({});
    const url = `api/file/${accountId}/attachments/account/${documentType}`;
    return this.http
      .get<FileSystemItem[]>(url, { headers: headers })
      .pipe(map((files) => this.processFiles(accountId, documentType, files)));
  }

  private processFiles(accountId: string, documentType: string, files: FileSystemItem[]): FileSystemItem[] {
    for (const file of files) {
      file.name = `${documentType}__${file.name}`;
      file.downloadUrl = `api/file/${accountId}/download-file/account/${file.name}`;
      // serverrelativeurl is already URL-encoded by the backend — encoding it
      // again here double-encodes it (e.g. %20 -> %2520), breaking the download.
      file.downloadUrl += `?serverRelativeUrl=${file.serverrelativeurl}&documentType=${documentType}`;
    }
    return files;
  }

  /**
   * Returns a summary of the current user's account. This contains basic high level information about the account and
   * all licences they have. This is useful for conditional logic that depends on a user having or not having certain
   * licences.
   *
   * Note: The supporting controller could be expanded to include other high level information, as needed.
   *
   * @return {*}  {Observable<AccountSummary>}
   */
  getAccountSummary(): Observable<AccountSummary> {
    return this.http
      .get<AccountSummary>(`${this.apiPath}current/summary`, { headers: this.headers })
      .pipe(catchError(this.handleError));
  }
}
